import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';

import { TopNav } from './components/top-nav';
import { AuthModal } from './components/auth-modal';
import type { AuthMode } from './components/auth-modal';
import { AuthGate, FullPageLoader } from './components/auth-gate';
import { Landing } from './pages/landing';
import { Onboarding } from './pages/onboarding';
import { Home } from './pages/home';
import { Guides } from './pages/guides';
import { GuideDetail } from './pages/guide-detail';
import { Profile } from './pages/profile';
import { Article } from './pages/article';
import { LanguagesPreview } from './pages/languages-preview';
import { Saved } from './pages/saved';
import { Explore } from './pages/explore';
import { Topics } from './pages/topics';
import { ChannelFeed } from './pages/channel-feed';
import { TagFeed } from './pages/tag-feed';
import { SearchResults } from './pages/search';
import { ThemeProvider } from './theme/theme-context';
import { useAuth, useAuthInit } from './hooks/use-auth';
import { useProfileQuery } from './hooks/use-profile-query';
import { useI18nSync } from './i18n/use-i18n-sync';
import { queryClient } from './config/query-client';

import './theme/tokens.css';
import './theme/app.css';

function Shell() {
  const navigate = useNavigate();
  const { user, isInitialized } = useAuth();
  const profile = useProfileQuery({ enabled: !!user });
  const [authMode, setAuthMode] = useState<AuthMode | null>(null);

  // Mirror the user's `primaryLanguage` (from the React Query profile cache)
  // into i18next whenever it changes. Pre-profile (unauthenticated) the app
  // stays on whatever locale init.ts resolved from localStorage / navigator.
  useI18nSync();

  // After auth, decide where to go: profile means feed, no profile means
  // onboarding. We watch user + profile and route once we have an answer.
  useEffect(() => {
    if (!user) return;
    if (profile.isLoading) return;
    const status = (profile.error as { statusCode?: number } | undefined)?.statusCode;
    if (status === 404 || (!profile.data && !profile.isFetching)) {
      // No profile yet — onboarding kicks in.
      const path = window.location.pathname;
      if (path === '/' || path.startsWith('/login') || path.startsWith('/signup')) {
        navigate('/onboarding', { replace: true });
      }
    } else if (profile.data) {
      const path = window.location.pathname;
      if (path === '/' || path.startsWith('/login') || path.startsWith('/signup') || path.startsWith('/onboarding')) {
        navigate('/home', { replace: true });
      }
    }
  }, [user, profile.data, profile.isLoading, profile.isFetching, profile.error, navigate]);

  const openAuth = useCallback((mode: AuthMode) => setAuthMode(mode), []);
  const closeAuth = useCallback(() => setAuthMode(null), []);

  // Show a brief splash while we're resolving the existing session — keeps
  // us from flashing the marketing landing for returning users.
  if (!isInitialized) return <FullPageLoader message="Reading the world…" />;

  return (
    <>
      <TopNav onLogin={() => openAuth('login')} onSignUp={() => openAuth('signup')} />
      <Routes>
        <Route
          path="/"
          element={
            user ? (
              <Navigate to="/home" replace />
            ) : (
              <Landing onSignUp={() => openAuth('signup')} onLogin={() => openAuth('login')} />
            )
          }
        />

        <Route
          path="/onboarding"
          element={
            <AuthGate requireProfile={false} fallback={<FullPageLoader message="Setting up your account…" />}>
              <Onboarding />
            </AuthGate>
          }
        />

        <Route
          path="/home"
          element={
            user ? (
              <AuthGate fallback={<FullPageLoader message="Loading your feed…" />}>
                <Home />
              </AuthGate>
            ) : (
              <Landing onSignUp={() => openAuth('signup')} onLogin={() => openAuth('login')} />
            )
          }
        />

        <Route
          path="/article/:id"
          element={
            <AuthGate fallback={<FullPageLoader message="Loading article…" />}>
              <Article />
            </AuthGate>
          }
        />

        <Route
          path="/saved"
          element={
            <AuthGate fallback={<FullPageLoader message="Loading saved articles…" />}>
              <Saved />
            </AuthGate>
          }
        />

        <Route path="/guides" element={<Guides onSignUp={() => openAuth('signup')} />} />

        <Route
          path="/guides/:id"
          element={
            <AuthGate fallback={<FullPageLoader message="Loading guide…" />}>
              <GuideDetail />
            </AuthGate>
          }
        />

        <Route
          path="/explore"
          element={
            <AuthGate fallback={<FullPageLoader message="Loading channels…" />}>
              <Explore />
            </AuthGate>
          }
        />

        <Route
          path="/topics"
          element={
            <AuthGate fallback={<FullPageLoader message="Loading topics…" />}>
              <Topics />
            </AuthGate>
          }
        />

        <Route
          path="/channel/:id"
          element={
            <AuthGate fallback={<FullPageLoader message="Loading channel…" />}>
              <ChannelFeed />
            </AuthGate>
          }
        />

        <Route
          path="/tag/:tag"
          element={
            <AuthGate fallback={<FullPageLoader message="Loading topic…" />}>
              <TagFeed />
            </AuthGate>
          }
        />

        <Route
          path="/search"
          element={
            <AuthGate fallback={<FullPageLoader message="Searching…" />}>
              <SearchResults />
            </AuthGate>
          }
        />

        <Route
          path="/profile"
          element={
            user ? (
              <AuthGate fallback={<FullPageLoader message="Loading your profile…" />}>
                <Profile />
              </AuthGate>
            ) : (
              <LandingWithAuthPrompt openAuth={openAuth} />
            )
          }
        />

        <Route
          path="/languages"
          element={user ? <Navigate to="/profile" replace /> : <LanguagesPreview onSignUp={() => openAuth('signup')} />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {authMode && (
        <AuthModal
          mode={authMode}
          onClose={closeAuth}
          onSwitchMode={setAuthMode}
          onAuthed={({ needsConfirmation }) => {
            if (needsConfirmation) return; // keep modal open with the info banner
            closeAuth();
          }}
        />
      )}
    </>
  );
}

function LandingWithAuthPrompt({ openAuth }: { readonly openAuth: (mode: AuthMode) => void }) {
  // Trigger the login modal once when the user lands here while signed-out.
  useEffect(() => {
    openAuth('login');
  }, [openAuth]);
  return <Landing onSignUp={() => openAuth('signup')} onLogin={() => openAuth('login')} />;
}

function AuthBoot({ children }: { readonly children: React.ReactNode }) {
  useAuthInit();
  return <>{children}</>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <AuthBoot>
          <BrowserRouter
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <Shell />
          </BrowserRouter>
        </AuthBoot>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
